import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { chatAPI, historyAPI } from '../api';

interface ChatContextType {
  claimGuestConversation: () => Promise<void>;
  guestId: string | null;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

  function generateGuestId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  // fallback → use getRandomValues to generate UUID-like string
  const array = new Uint32Array(4);
  window.crypto.getRandomValues(array);
  return (
    array[0].toString(16) +
    array[1].toString(16) +
    array[2].toString(16) +
    array[3].toString(16)
  );
}

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refreshUser } = useAuth();
  const [isClaimingGuest, setIsClaimingGuest] = useState(false);
  const historyLoadedRef = useRef(false);

  // Generate and store guest_id for unauthenticated users
  const [guestId, setGuestId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('guest_id');
      if (stored) return stored;
      
      // Generate new guest_id if none exists


const newGuestId = generateGuestId();
localStorage.setItem("guest_id", newGuestId);

      return newGuestId;
    }
    return null;
  });

  // Clear guest_id when user logs in, but only if not currently claiming
  React.useEffect(() => {
    if (user && guestId && !isClaimingGuest) {
      localStorage.removeItem('guest_id');
      setGuestId(null);
    }
  }, [user?.id, user?.email, guestId, isClaimingGuest]);

  const claimGuestConversation = useCallback(async () => {
    if (!user || !guestId) {
      console.log('claimGuestConversation: Cannot claim - missing user or guestId', { user: !!user, guestId: !!guestId });
      return;
    }

    try {
      console.log('claimGuestConversation: Starting claim process', { guestId, userId: user.id });
      setIsClaimingGuest(true);
      const response = await chatAPI.claimGuestConversation(guestId);
      console.log('Guest conversation claimed successfully:', response);
      
      // Clear guest_id from localStorage
      localStorage.removeItem('guest_id');
      setGuestId(null);
      
      // Reset historyLoadedRef so user history gets loaded after claiming
      historyLoadedRef.current = false;
      console.log('claimGuestConversation: Reset historyLoadedRef, calling refreshUser');
      
      // Always refresh user after claiming; useEffect will reload chat history
      await refreshUser();
      console.log('claimGuestConversation: refreshUser completed');
    } catch (err: any) {
      console.error('Error claiming guest conversation:', err);
    } finally {
      setIsClaimingGuest(false);
      console.log('claimGuestConversation: Completed, isClaimingGuest set to false');
    }
  }, [user, guestId, refreshUser]);

  const value: ChatContextType = {
    claimGuestConversation,
    guestId,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}; 